/**
 * Get the full URL for an image with optional optimization
 * Handles both Cloudinary URLs and local upload paths
 * @param {string} imagePath - The image path or URL
 * @param {object} options - Optimization options
 * @param {number} options.width - Desired width (for Cloudinary transformations)
 * @param {number} options.height - Desired height (for Cloudinary transformations)
 * @param {number} options.quality - Image quality (1-100, default: auto)
 * @param {string} options.format - Image format (auto, webp, jpg, png)
 * @param {string} options.crop - Crop mode (fill, fit, scale, etc.)
 */
const normalizeImagePath = (input) => {
  if (!input) return '';

  // Handle common object shapes returned by upload providers/APIs
  if (typeof input === 'object') {
    const candidate = input.secure_url || input.url || input.path || input.src || '';
    return normalizeImagePath(candidate);
  }

  if (typeof input !== 'string') return '';

  // Trim and remove accidental wrapping quotes
  const trimmed = input.trim();
  if (!trimmed) return '';
  return trimmed.replace(/^['"]+|['"]+$/g, '');
};

const isAbsoluteUrl = (value) =>
  value.startsWith('http://') || value.startsWith('https://') || value.startsWith('//');

const UNSPLASH_PRODUCT_FALLBACK =
  'https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=900&q=80';
const UNSPLASH_PROFILE_FALLBACK =
  'https://images.unsplash.com/photo-1544723795-3fb6469f5b39?auto=format&fit=crop&w=600&q=80';

export const getImageUrl = (imagePath, options = {}) => {
  const normalizedPath = normalizeImagePath(imagePath);

  if (!normalizedPath) {
    return UNSPLASH_PRODUCT_FALLBACK;
  }

  // If it's already a full URL (Cloudinary or external)
  if (isAbsoluteUrl(normalizedPath)) {
    const absoluteUrl = normalizedPath.startsWith('//') ? `https:${normalizedPath}` : normalizedPath;

    // Keep Cloudinary URLs as-is so uploaded product images render correctly.
    // (Signed URLs may include signatures/query params that should not be altered here.)
    if (absoluteUrl.includes('res.cloudinary.com')) {
      return absoluteUrl;
    }
    return absoluteUrl;
  }

  // Get the API base URL (without /api)
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  let baseUrl = apiUrl.replace('/api', '');
  
  // Remove trailing slash if present
  if (baseUrl.endsWith('/')) {
    baseUrl = baseUrl.slice(0, -1);
  }

  // If it starts with /uploads, construct full URL
  if (normalizedPath.startsWith('/uploads')) {
    return `${baseUrl}${normalizedPath}`;
  }

  // If it doesn't start with /, assume it's a relative path from uploads
  if (!normalizedPath.startsWith('/')) {
    return `${baseUrl}/uploads/${normalizedPath}`;
  }

  // Default: assume it's a path from backend (starts with /)
  return `${baseUrl}${normalizedPath}`;
};

/**
 * Get optimized product image URL for card display
 */
export const getProductImageUrl = (imagePath, size = 'medium') => {
  const sizes = {
    small: { width: 300, height: 300 },
    medium: { width: 500, height: 500 },
    large: { width: 800, height: 800 },
    thumbnail: { width: 200, height: 200 }
  };

  const dimensions = sizes[size] || sizes.medium;
  return getImageUrl(imagePath, {
    ...dimensions,
    quality: 80,
    format: 'auto',
    crop: 'fill'
  });
};

/**
 * Get profile image URL with fallback to default.
 * Handles both local uploads and external URLs (e.g. Google profile picture).
 * Use with referrerPolicy="no-referrer" on <img> so external images load on all pages.
 * Optionally adds a cache-busting parameter to force image refresh.
 */
export const getProfileImageUrl = (profileImage, bustCache = false) => {
  const normalizedPath = normalizeImagePath(profileImage);
  if (!normalizedPath) {
    return UNSPLASH_PROFILE_FALLBACK;
  }
  // External URLs (Google, etc.) – return as-is so they work everywhere with no-referrer
  if (isAbsoluteUrl(normalizedPath)) {
    const absoluteUrl = normalizedPath.startsWith('//') ? `https:${normalizedPath}` : normalizedPath;
    if (bustCache) {
      const separator = absoluteUrl.includes('?') ? '&' : '?';
      return `${absoluteUrl}${separator}t=${Date.now()}`;
    }
    return absoluteUrl;
  }
  const imageUrl = getImageUrl(normalizedPath);
  if (bustCache && imageUrl !== UNSPLASH_PROFILE_FALLBACK) {
    const separator = imageUrl.includes('?') ? '&' : '?';
    return `${imageUrl}${separator}t=${Date.now()}`;
  }
  return imageUrl;
};

